import React, { useMemo, useState } from 'react';

/* ============================================================
   NoteContent — structured renderer for free-text coaching
   notes, built for imported diet protocols and long write-ups.
   Lines are classified into headings ("Allowed Food List:",
   "Meal 1: Breakfast"), bullets ("- Drink water…"), and
   label rows ("Proteins: Grass-Fed Beef, …") so a wall of
   text reads like the document it actually is. Long notes
   collapse to a preview with Show more.
============================================================ */

type Line =
  | { kind: 'heading'; text: string }
  | { kind: 'bullet'; text: string }
  | { kind: 'kv'; label: string; text: string }
  | { kind: 'plain'; text: string };

const classify = (raw: string): Line | null => {
  const t = raw.trim();
  if (!t) return null;
  const unbulleted = t.replace(/^[-•–—]\s*/, '');
  const isBullet = unbulleted !== t;
  const kv = unbulleted.match(/^([^:]{2,42}):\s*(.*)$/);

  if (!isBullet && kv && !kv[2]) return { kind: 'heading', text: kv[1] };
  // "Meal 1: Breakfast" / "Pre-Meal: Fasted" — short labelled headings
  if (!isBullet && kv && kv[2] && kv[1].split(/\s+/).length <= 4 && kv[2].length <= 32 && !kv[2].includes(',')) {
    return { kind: 'heading', text: `${kv[1]} — ${kv[2]}` };
  }
  if (kv && kv[2]) return { kind: 'kv', label: kv[1], text: kv[2] };
  if (isBullet) return { kind: 'bullet', text: unbulleted };
  return { kind: 'plain', text: t };
};

const NoteContent: React.FC<{ text: string; collapsedLines?: number }> = ({ text, collapsedLines = 12 }) => {
  const [open, setOpen] = useState(false);
  const lines = useMemo(
    () => text.split('\n').map(classify).filter((l): l is Line => l !== null),
    [text]
  );
  const collapsible = lines.length > collapsedLines + 3;
  const shown = collapsible && !open ? lines.slice(0, collapsedLines) : lines;

  return (
    <div>
      <div className="flex flex-col gap-1">
        {shown.map((l, i) => {
          switch (l.kind) {
            case 'heading':
              return (
                <p key={i} className={`text-[11px] font-bold uppercase tracking-[0.1em] text-fit-text-secondary ${i === 0 ? '' : 'mt-3'}`}>
                  {l.text}
                </p>
              );
            case 'bullet':
              return (
                <p key={i} className="text-[13px] leading-relaxed text-fit-text-secondary pl-3 relative">
                  <span className="absolute left-0 text-fit-muted">•</span>
                  {l.text}
                </p>
              );
            case 'kv':
              return (
                <p key={i} className="text-[13px] leading-relaxed pl-3">
                  <span className="font-semibold text-white">{l.label}: </span>
                  <span className="text-fit-text-secondary">{l.text}</span>
                </p>
              );
            default:
              return (
                <p key={i} className="text-[13px] leading-relaxed text-fit-text-secondary">
                  {l.text}
                </p>
              );
          }
        })}
      </div>
      {collapsible && (
        <button
          onClick={() => setOpen((o) => !o)}
          className="text-[11px] text-fit-text-secondary hover:text-white underline underline-offset-2 mt-2"
        >
          {open ? 'Show less ↑' : `Show all ${lines.length} lines ↓`}
        </button>
      )}
    </div>
  );
};

export default NoteContent;
